import { wait } from "@/utils";
import { CameraStore, useCamera } from "camera/store/camera";

export async function getCameraAndMicrophoneInfo() {
  try {
    const deviceList = await navigator.mediaDevices.enumerateDevices();

    const videoDevices = deviceList.filter((device) => device.kind === "videoinput");
    const audioDevices = deviceList.filter((device) => device.kind === "audioinput");
    const audioOuputDevices = deviceList.filter((device) => device.kind === "audiooutput");

    return {
      cameraId: videoDevices[0].deviceId,
      microphoneId: audioDevices[0].deviceId,
      audioOutputs: audioOuputDevices,
      cameraList: videoDevices
    };
  } catch (err) {
    log.err("Couldn't get device list from navigator");
  }
}

export function createDeviceChangeHandler(audioOutputDevices?: MediaDeviceInfo[]) {
  let { cameraId, microphoneId, cameraList } = useCamera.getState();

  const compareDevices = async () => {
    log.info("'devicechange' fired, comparing devices...");
    const availableDevices = await navigator.mediaDevices.enumerateDevices();
    const availableVideoDevices = availableDevices.filter((device) => device.kind === "videoinput");
    const availableAudioDevices = availableDevices.filter((device) => device.kind === "audioinput");
    const availableAudioOutputDevices = availableDevices.filter((device) => device.kind === "audiooutput");

    const isCameraAvailable = availableVideoDevices.some((device) => device.deviceId === cameraId);
    const isMicrophoneAvailable = availableAudioDevices.some((device) => device.deviceId === microphoneId);

    if (
      (!cameraId && availableVideoDevices.length > 0) ||
      (!microphoneId && availableAudioDevices.length > 0) ||
      (cameraId && !isCameraAvailable) ||
      (microphoneId && !isMicrophoneAvailable) ||
      (audioOutputDevices && audioOutputDevices.length !== availableAudioOutputDevices.length)
    ) {
      if (isDev) {
        log.warn("Change in available devices detected, restarting in 10 seconds...");
        await wait(10000);
        restartApp({ cause: "media-devices-changed" });
      } else restartApp({ cause: "media-devices-changed" });
      return;
    }

    const availableCameraIds = availableVideoDevices.map((device) => device.deviceId).join();
    const listedCameraIds = cameraList?.map((device) => device.deviceId).join();
    if (availableCameraIds !== listedCameraIds) {
      log.info("Available cameras changed, updating the camera list", availableVideoDevices);
      useCamera.getState().setCameraList(availableVideoDevices);
    }
  };

  const onStoreUpdate = (store: CameraStore) => {
    cameraId = store.cameraId;
    microphoneId = store.microphoneId;
    cameraList = store.cameraList;
  };

  useCamera.subscribe(onStoreUpdate);
  navigator.mediaDevices.addEventListener("devicechange", compareDevices);
}
